import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Wind, Wifi, Tv, Phone, Bath, ShieldCheck, Users, BedDouble } from "lucide-react";
import { listRoomsAndBlocks } from "@/lib/booking.functions";
import { computeEffectivePrice, type Season } from "@/lib/pricing";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/2-double-beds-room-crystal-beach")({
  head: () => ({
    meta: [
      { title: "2 Double Beds Room - Malak BnB Resort, Crystal Beach" },
      { name: "description", content: "The 2 Double Beds room at Malak BnB Resort in Crystal Beach, Ontario. 450 sq ft, from C$345 a night, breakfast and pool included. Request this room." },
      { property: "og:title", content: "2 Double Beds Room - Malak BnB Resort, Crystal Beach" },
      { property: "og:description", content: "A 450 sq ft room with 2 double beds, five minutes from Bay Beach. Breakfast, pool, and free Wi-Fi included at Malak BnB Resort." },
      { property: "og:url", content: "https://malakbnbresort.ca/2-double-beds-room-crystal-beach" },
      { property: "og:image", content: "/images/room-double-beds.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Rooms", path: "/rooms" },
          { name: "2 Double Beds", path: "/2-double-beds-room-crystal-beach" },
        ]),
      },
    ],
    links: [canonical("/2-double-beds-room-crystal-beach")],
  }),
  component: DoubleBedsRoomPage,
});

const AMENITIES = [
  { icon: Wind, label: "Air Conditioning" },
  { icon: Wifi, label: "Free Wi-Fi" },
  { icon: Tv, label: "Flat Screen TV" },
  { icon: Phone, label: "In-Room Phone" },
  { icon: Bath, label: "Private Bathroom" },
  { icon: ShieldCheck, label: "In-Room Safe" },
];

const FAQ = [
  {
    q: "Who is the 2 Double Beds room at Malak BnB Resort best for?",
    a: "The 2 Double Beds room at Malak BnB Resort is best for families, friends travelling together, or anyone who wants two separate beds instead of sharing one. At 450 sq ft, it is the largest room the property offers, with space to spread out after a day at Bay Beach.",
  },
  {
    q: "What is included with the 2 Double Beds room?",
    a: "The 2 Double Beds room includes fine bedding, a flat screen TV with over 50 stations, a night table, a dresser, a phone, hardwood flooring, air conditioning, free Wi-Fi, and an in-room safe. Continental breakfast and outdoor pool access are included with every stay at Malak BnB Resort.",
  },
  {
    q: "How do I request the 2 Double Beds room at Malak BnB Resort?",
    a: "Submit a reservation request through the Malak BnB Resort booking form with your preferred dates and this room selected. The team personally reviews and confirms availability, then sends payment details directly. There is no instant booking.",
  },
];

function DoubleBedsRoomPage() {
  const listFn = useServerFn(listRoomsAndBlocks);
  const { data } = useQuery({ queryKey: ["rooms-and-blocks"], queryFn: () => listFn() });
  const today = new Date();
  const dbRoom = data?.rooms.find((r) => r.name === "2 Double Beds");
  const price = dbRoom
    ? `C$${computeEffectivePrice(Number(dbRoom.price_cad), today, (data?.seasons ?? []) as Season[])}`
    : "C$345";

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] overflow-hidden">
        <img
          src="/images/room-double-beds.webp"
          alt="2 Double Beds room at Malak BnB Resort in Crystal Beach, Ontario"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/10 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-14 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/rooms" className="hover:text-white transition-colors">Rooms</Link>
              <span>/</span>
              <span>2 Double Beds</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight">
              2 Double Beds Room at Malak BnB Resort
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">Crystal Beach, Ontario</p>
          <p className="text-2xl font-heading text-foreground leading-snug mb-8">
            Two double beds, fine bedding, and enough room to actually unpack.
          </p>
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              This is the biggest room at Malak BnB Resort, and it shows the moment you walk in. Two double beds sit
              on hardwood floors, dressed in fine bedding with plenty of pillows. A flat screen TV with over 50
              stations, a night table, a dresser, and a phone round out the room.
            </p>
            <p>
              At 450 sq ft, there is genuine breathing room here. It suits families splitting up kids and parents,
              two couples travelling together, or a small group who would rather not share a bed. Everyone gets a
              proper night of sleep, and there is still floor space for suitcases and beach towels drying by the window.
            </p>
            <p>
              Book it for a Crystal Beach weekend and you are five minutes on foot from Bay Beach, with the outdoor
              heated pool and continental breakfast waiting back at the property.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Request This Room
            </Link>
            <Link
              to="/rooms"
              className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
            >
              See All Rooms and Rates
            </Link>
          </div>
        </div>
      </section>

      {/* Details card */}
      <section className="pb-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="bg-card border border-border/50 rounded-2xl p-8 sm:p-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div className="flex items-start gap-3">
              <BedDouble className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Sleeping</p>
                <p className="text-foreground font-medium mt-1">2 Double Beds</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Room Size</p>
                <p className="text-foreground font-medium mt-1">450 sq ft</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Rate</p>
              <p className="text-2xl font-heading text-foreground mt-1">From {price}</p>
              <p className="text-xs text-muted-foreground">per night, rates vary by season</p>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl font-heading text-foreground mb-10">What's In the Room</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {AMENITIES.map((a) => (
              <div key={a.label} className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-background border border-border/50">
                <a.icon className="h-6 w-6 text-primary" />
                <span className="text-sm font-medium text-foreground text-center">{a.label}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8">
            Every stay at Malak BnB Resort also includes continental breakfast and access to the outdoor heated pool.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">About This Room</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/crystal-beach-bed-and-breakfast" className="text-primary font-semibold text-sm hover:underline">
            Read more about staying at this Crystal Beach BnB →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Request the 2 Double Beds room at Malak BnB Resort
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request to Book
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
